import { message } from "antd"
import { Service } from "../service"

/**
 * Obtiene los datos del usuario actual
 * @param {string} token - Token de autenticación
 * @returns {Promise<Object|null>} - Datos del usuario o null en caso de error
 */
export const fetchCurrentUser = async (token) => {
	if (!token) {
		return null
	}

	try {
		const service = new Service()
		const userData = await service.getMe({ token })
		return userData
	} catch (error) {
		message.error("Error al obtener datos del usuario")
		console.error(error)
		return null
	}
}

/**
 * Actualiza los datos del usuario actual
 * @param {string} token - Token de autenticación
 * @param {Object} userData - Datos a actualizar
 * @param {Function} updateUserInfo - Función para actualizar la información en localStorage
 * @returns {Promise<Object|boolean>} - Datos actualizados o false en caso de error
 */
export const updateCurrentUser = async (token, userData, updateUserInfo) => {
	if (!token) {
		return false
	}

	try {
		// Filtrar datos sensibles o innecesarios
		const { email, password, roles, pharmacy, ...filteredData } = userData

		const service = new Service()
		const updatedUser = await service.updateMe({
			token,
			data: filteredData,
		})

		if (updatedUser) {
			if (updateUserInfo) {
				updateUserInfo(updatedUser)
			}
			message.success("Datos actualizados correctamente")
			return updatedUser
		}

		throw new Error("No se recibió una respuesta válida del servidor")
	} catch (error) {
		message.error("Error al actualizar los datos: " + (error.message || "Error desconocido"))
		console.error(error)
		return false
	}
}

/**
 * Obtiene todos los usuarios
 * @param {string} token - Token de autenticación
 * @returns {Promise<Array>} - Lista de usuarios o array vacío en caso de error
 */
export const fetchAllUsers = async (token) => {
	if (!token) {
		return []
	}

	try {
		const service = new Service()
		const users = await service.getUsers({ token })
		return users || []
	} catch (error) {
		message.error("Error al obtener la lista de usuarios")
		console.error(error)
		return []
	}
}

/**
 * Actualiza el estado de un usuario
 * @param {string} token - Token de autenticación
 * @param {string} userId - ID del usuario
 * @param {Object} userData - Datos a actualizar
 * @returns {Promise<boolean>} - true si la actualización fue exitosa, false en caso contrario
 */
export const updateUserStatus = async (token, userId, userData) => {
	if (!token) {
		return false
	}

	try {
		const service = new Service()
		const response = await service.updateUser({
			token,
			data: userData,
		})

		if (response) {
			message.success("Usuario actualizado correctamente")
			return true
		}

		throw new Error("Error al actualizar el usuario")
	} catch (error) {
		message.error("Error al actualizar el usuario: " + (error.message || "Error desconocido"))
		console.error(error)
		return false
	}
}

/**
 * Crea un nuevo usuario
 * @param {string} token - Token de autenticación
 * @param {Object} userData - Datos del nuevo usuario
 * @returns {Promise<Object|boolean>} - Usuario creado o false en caso de error
 */
export const createUser = async (token, userData) => {
	if (!token) {
		return false
	}

	try {
		const service = new Service()
		const response = await service.createUser({
			token,
			data: userData,
		})

		if (response) {
			message.success("Usuario creado correctamente")
			return response
		}

		throw new Error("Error al crear el usuario")
	} catch (error) {
		message.error("Error al crear el usuario: " + (error.message || "Error desconocido"))
		console.error(error)
		return false
	}
}

/**
 * Actualiza la contraseña de un usuario
 * @param {string} token - Token de autenticación
 * @param {string} oldPassword - Contraseña actual
 * @param {string} newPassword - Nueva contraseña
 * @returns {Promise<boolean>} - true si la actualización fue exitosa, false en caso contrario
 */
export const changePassword = async (token, oldPassword, newPassword) => {
	if (!token) {
		return false
	}

	try {
		const service = new Service()
		const response = await service.changePassword({
			token,
			data: {
				old_password: oldPassword,
				new_password: newPassword,
			}
		})

		if (response) {
			message.success("Contraseña actualizada correctamente")
			return true
		}

		throw new Error("Error al actualizar la contraseña")
	} catch (error) {
		message.error("Error al actualizar la contraseña: " + (error.message || "Error desconocido"))
		console.error(error)
		return false
	}
}

/**
 * Busca un usuario por email
 * @param {string} token - Token de autenticación
 * @param {string} email - Email del usuario
 * @returns {Promise<Object|null>} - Usuario encontrado o null en caso de error
 */
export const findUserByEmail = async (token, email) => {
	if (!token) {
		return null
	}

	try {
		const service = new Service()
		const user = await service.findUser({ token, email })
		return user
	} catch (error) {
		message.error("Error al buscar el usuario")
		console.error(error)
		return null
	}
}